import { useEffect, useState } from 'react';
import axios from '../../api/axios';
import { toast } from 'react-toastify';

const card = {
  background: 'white', padding: '24px',
  borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
};
const th = {
  padding: '14px', background: '#1a237e',
  color: 'white', textAlign: 'left', fontSize: '14px'
};
const td = {
  padding: '14px', borderBottom: '1px solid #eee', fontSize: '14px'
};
const badge = {
  display: 'inline-block', padding: '4px 10px',
  borderRadius: '20px', fontSize: '12px', fontWeight: '600',
  background: '#eef2ff', color: '#1a237e',
  marginRight: '6px', marginBottom: '6px'
};

const RemunerationCalculator = () => {
  const [shifts, setShifts]       = useState([]);
  const [mappings, setMappings]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [grandTotal, setGrandTotal] = useState(0);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [shiftRes, mappingRes] = await Promise.all([
        axios.get('/shifts'),
        axios.get('/invigilators/invigilator-mapping')
      ]);

      const shiftData   = shiftRes.data   || [];
      const mappingData = mappingRes.data || [];

      const updatedShifts = shiftData.map((shift) => {
        const matchedMapping       = mappingData.find((m) => m.shift_id === shift.id);
        const assignedInvigilators = matchedMapping?.invigilators || [];
        // Only count invigilators who were present for remuneration
        const presentCount = assignedInvigilators.filter((inv) => inv.is_present).length;

        return {
          ...shift,
          assigned_invigilators: assignedInvigilators,
          invigilator_count:     assignedInvigilators.length,
          present_count:         presentCount,
        };
      });

      setShifts(updatedShifts);
      setMappings(mappingData);

      // Note: for 'per_day' exams this is only an estimate — the authoritative
      // total comes from the remuneration table (built in the backend), since
      // that's where the "pay once per day" logic actually lives.
      const total = updatedShifts.reduce((acc, shift) => {
        const rate = Number(shift.remuneration_rate || 0);
        return acc + (Number(shift.present_count || 0) * rate);
      }, 0);
      setGrandTotal(total);

    } catch (err) {
      console.log(err);
      toast.error('Failed to load remuneration data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '32px', background: '#f5f6fa', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ color: '#1a237e', marginBottom: '8px' }}>
          Faculty & Invigilator Remuneration
        </h2>
        <p style={{ color: '#666' }}>View remuneration and assigned invigilators</p>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit,minmax(250px,1fr))',
        gap: '20px', marginBottom: '24px'
      }}>
        <div style={card}>
          <h3 style={{ marginBottom: '10px', color: '#555' }}>Total Shifts</h3>
          <h1 style={{ color: '#1a237e' }}>{shifts.length}</h1>
        </div>
        <div style={card}>
          <h3 style={{ marginBottom: '10px', color: '#555' }}>Total Remuneration (est.)</h3>
          <h1 style={{ color: 'green' }}>₹ {grandTotal.toFixed(2)}</h1>
        </div>
      </div>

      {/* Remuneration Table */}
      <div style={card}>
        <h3 style={{ marginBottom: '20px', color: '#1a237e' }}>Shift-wise Remuneration</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Exam</th>
                <th style={th}>Shift</th>
                <th style={th}>Date</th>
                <th style={th}>Pay Type</th>
                <th style={th}>Assigned</th>
                <th style={th}>Present</th>
                <th style={th}>Assigned Invigilators</th>
                <th style={th}>Rate</th>
                <th style={th}>Amount Earned</th>
              </tr>
            </thead>
            <tbody>
              {shifts.length > 0 ? shifts.map((shift) => {
                const rate   = Number(shift.remuneration_rate || 0);
                const earned = Number(shift.present_count || 0) * rate;
                const isPerDay = shift.remuneration_type === 'per_day';

                return (
                  <tr key={shift.id}>
                    <td style={td}>{shift.exam_name}</td>
                    <td style={td}>{shift.shift_name}</td>
                    <td style={td}>{shift.shift_date}</td>
                    <td style={td}>
                      <span style={{
                        ...badge,
                        background: isPerDay ? '#fff8e1' : '#eef2ff',
                        color: isPerDay ? '#f57f17' : '#1a237e'
                      }}>
                        {isPerDay ? 'Per Day' : 'Per Shift'}
                      </span>
                    </td>
                    <td style={td}>{shift.invigilator_count}</td>
                    <td style={td}>{shift.present_count}</td>
                    <td style={td}>
                      {shift.assigned_invigilators.length > 0 ? (
                        shift.assigned_invigilators.map((inv) => (
                          <span key={inv.id} style={{
                            ...badge,
                            background: inv.is_present ? '#e8f5e9' : '#fff8e1',
                            color:      inv.is_present ? '#2e7d32' : '#f57f17'
                          }}>
                            {inv.name} {inv.is_present ? '✓' : '✗'}
                          </span>
                        ))
                      ) : (
                        <span style={{ color: '#999' }}>No invigilators assigned</span>
                      )}
                    </td>
                    <td style={td}>₹ {rate.toFixed(2)}</td>
                    <td style={{ ...td, fontWeight: 'bold', color: 'green' }}>
                      ₹ {earned.toFixed(2)}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="9" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    {loading ? 'Loading...' : 'No remuneration data found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {shifts.some(s => s.remuneration_type === 'per_day') && (
          <p style={{ fontSize: '12px', color: '#999', marginTop: '12px' }}>
            Amounts shown here are per-shift estimates. For exams marked "Per Day", the actual
            payout (from the remuneration table) pays each invigilator once per day at their
            highest applicable rate — check the Summary/Weekly/Monthly reports for the exact total.
          </p>
        )}
      </div>

      {/* Mapping Details */}
      <div style={{ ...card, marginTop: '30px' }}>
        <h3 style={{ marginBottom: '20px', color: '#1a237e' }}>Invigilator Mapping Details</h3>
        {mappings.length > 0 ? mappings.map((mapping) => (
          <div key={mapping.shift_id} style={{
            border: '1px solid #eee', borderRadius: '10px',
            padding: '18px', marginBottom: '18px', background: '#fafafa'
          }}>
            <h4 style={{ marginBottom: '10px', color: '#1a237e' }}>{mapping.shift_name}</h4>
            <p style={{ color: '#666', marginBottom: '12px' }}>Date: {mapping.shift_date}</p>
            <div>
              {mapping.invigilators.map((inv) => (
                <div key={inv.id} style={{
                  padding: '10px', marginBottom: '8px',
                  borderRadius: '8px', background: 'white', border: '1px solid #eee'
                }}>
                  <strong>{inv.name}</strong>
                  <p style={{ margin: '4px 0', color: '#666' }}>{inv.email}</p>
                  <span style={{ color: inv.is_present ? 'green' : 'orange', fontWeight: '600' }}>
                    {inv.is_present ? 'Present' : 'Not Marked'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )) : (
          <p style={{ color: '#999' }}>No mappings available</p>
        )}
      </div>

    </div>
  );
};

export default RemunerationCalculator;